import React from 'react';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { formatCurrencyForCSV as formatCurrency } from '../../lib/helpers/formatters';
import { BtnOutlineOrange } from '../../styled-components';
import { IAFType } from '../../lib/constants';
import { useAuth } from '../Auth';
import axios from 'axios';
import { useAlert } from 'react-alert';
import { useRef } from 'react';
import { useLoading } from '../Loading';

const ExportIAFCSVField = ({ data }) => {
  const [t] = useTranslation();
  const alert = useAlert();
  const [{ newToken } = {}] = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const { iafType } = data;

  const csvLinkRef = useRef();
  const [csvData, setCsvData] = React.useState([]);

  const handleExport = async () => {
    let services = [];
    if (data.commercial === true) {
      try {
        services =
          iafType === IAFType.INSURANCES
            ? data.insurances
            : iafType === IAFType.INVOICES
            ? data.invoices
            : iafType === IAFType.ENERGY
            ? data.contracts
            : iafType === IAFType.RECURRENT_ENERGY
            ? data.recurrentContracts
            : [];

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/bonuses/export/iaf/network`,
          {
            services: services.map((service) => {
              return {
                _id: service._id,
                earnedCommercialShare: service.earnedCommercialShare,
              };
            }),
            iafType: iafType,
          },
          {
            headers: { Authorization: newToken },
          }
        );
        services = response.data.services || [];
      } catch (error) {
        alert.error(t('COMMON:GENERIC_ERROR'));
        return;
      }
    } else {
      services =
        iafType === IAFType.INSURANCES
          ? data.insurances
          : iafType === IAFType.INVOICES
          ? data.invoices
          : iafType === IAFType.ENERGY
          ? data.contracts
          : iafType === IAFType.RECURRENT_ENERGY
          ? data.recurrentContracts
          : iafType === IAFType.SIGNALS
          ? data.signals
          : [];
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/bonuses/export/iaf`,
          {
            services: services.map((service) => {
              return {
                _id: service._id,
                adminShare: service.adminShare || 0,
                earnedAdminShare: service.earnedAdminShare || 0,
              };
            }),
            iafType: iafType,
          },
          {
            headers: { Authorization: newToken },
          }
        );
        services = response.data.services || [];
      } catch (error) {
        alert.error(t('COMMON:GENERIC_ERROR'));
        return;
      }
    }

    const csv =
      data.commercial === true
        ? getCSVNetworkData({ data, services })
        : getCSVData({ data, services });
    setCsvData(csv);
    setTimeout(() => {
      csvLinkRef.current.link.click();
    }, 0);
  };

  if (!data.iafType) return '';

  return (
    <>
      <BtnOutlineOrange
        type="button"
        className="uppercase"
        onClick={handleExport}
      >
        {t('COMMON:CSV_EXPORT')}
      </BtnOutlineOrange>
      <CSVLink
        data={csvData}
        filename={`IAF_${data._id?.toUpperCase()}.csv`}
        separator=";"
        ref={csvLinkRef}
        style={{ display: 'none' }}
      />
    </>
  );
};
const getCSVData = ({ data = {}, services = [] }) => {
  const { amount, iafType } = data;
  const isAssociation = data.profile[0]?.type === 'ASSOCIATION';

  if (iafType === IAFType.INVOICES || iafType === IAFType.INVOICES_SUB_ADMINS) {
    const CSVData = [
      [
        'NUMERO FATTURA',
        'DATA',
        'RESPONSABILE',
        'CODICE FISCALE RESPONSABILE',
        'FORNITORE',
        'CODICE FISCALE FORNITORE',
        'CLIENTE',
        'CODICE FISCALE CLIENTE',
        'N. STUDIO',
        ...(isAssociation
          ? [
              'RAGIONE SOCIALE UFFICIO',
              'UFFICIO',
              'EMAIL PEC UFFICIO',
              'PERCENTUALE UFFICIO',
            ]
          : []),
        'MESE RENDICONTAZIONE',
        'ESERCIZIO DI RIFERIMENTO',
        'IMPONIBILE',
        'QUOTA RESPONSABILE (%)',
        'QUOTA RESPONSABILE MATURATA',
        'INVITATO A FATTURARE',
      ],
    ];

    services.forEach(
      ({
        number,
        date,
        admin,
        supplier,
        customer,
        accountability,
        net,
        adminShare,
        earnedAdminShare,
      }) => {
        CSVData.push([
          number,
          moment(date).format('DD/MM/yyyy'),
          admin?.businessName,
          admin?.taxCode,
          supplier?.businessName,
          supplier?.taxCode,
          customer?.type === 'AZIENDA'
            ? customer?.businessName
            : customer?.type === 'PRIVATO'
            ? `${customer?.name} ${customer?.surname}`
            : customer?.name,
          customer?.vatCode ? customer?.vatCode : customer?.taxCode,
          customer?.studioNumber ? customer?.studioNumber : '-',
          ...(isAssociation
            ? [
                customer.office?.businessName,
                customer.office?.office,
                customer.office?.email,
                data.profile[0]?.officePerc || 0,
              ]
            : []),
          accountability ? moment(accountability).format('MM/yyyy') : '',
          data.accountability,
          formatCurrency(+net),
          formatCurrency(iafType === IAFType.INVOICES ? 10 : 1),
          formatCurrency(+adminShare),
          formatCurrency(+earnedAdminShare),
        ]);
      }
    );
    CSVData.push([
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      ...(isAssociation ? ['-', '-', '-', '-'] : []),
      '-',
      '-',
      '-',
      '-',
      '-',
      formatCurrency(+amount),
    ]);

    return CSVData || [];
  }

  if (iafType === IAFType.ENERGY) {
    const CSVData = [
      [
        'POD_PDR',
        'RESPONSABILE',
        'CODICE FISCALE RESPONSABILE',
        'FORNITORE',
        'CODICE FISCALE FORNITORE',
        'CLIENTE',
        'CODICE FISCALE CLIENTE',
        'N. STUDIO',
        ...(isAssociation
          ? [
              'RAGIONE SOCIALE UFFICIO',
              'UFFICIO',
              'EMAIL PEC UFFICIO',
              'PERCENTUALE UFFICIO',
            ]
          : []),
          'DATA CREAZIONE',
          'ESERCIZIO DI RIFERIMENTO',
        'QUOTA RESPONSABILE MATURATA',
        'INVITATO A FATTURARE',
      ],
    ];

    services.forEach(
      ({
        pod_pdr,
        admin,
        customer,
        supplier,
        adminShare,
        earnedAdminShare,
        contract_signing_date,
      }) => {
        CSVData.push([
          pod_pdr,
          admin?.businessName,
          admin?.taxCode,
          supplier?.businessName,
          supplier?.taxCode,
          customer?.type === 'AZIENDA'
            ? customer?.businessName
            : customer?.type === 'PRIVATO'
            ? `${customer?.name} ${customer?.surname}`
            : customer?.name,
          customer?.vatCode ? customer?.vatCode : customer?.taxCode,
          customer?.studioNumber ? customer?.studioNumber : '-',
          ...(isAssociation
            ? [
                customer.office?.businessName,
                customer.office?.office,
                customer.office?.email,
                data.profile[0]?.officePerc || 0,
              ]
            : []),
            contract_signing_date ? moment(contract_signing_date).format('DD/MM/YYYY') : '',
            data.accountability,
          formatCurrency(+adminShare),
          formatCurrency(+earnedAdminShare),
        ]);
      }
    );
    CSVData.push([
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      ...(isAssociation ? ['-', '-', '-', '-'] : []),
      '-',
      '-',
      formatCurrency(+amount),
    ]);

    return CSVData || [];
  }

  if (iafType === IAFType.RECURRENT_ENERGY) {
    const CSVData = [
      [
        'POD_PDR',
        'RESPONSABILE',
        'CODICE FISCALE RESPONSABILE',
        'FORNITORE',
        'CODICE FISCALE FORNITORE',
        'CLIENTE',
        'CODICE FISCALE CLIENTE',
        'N. STUDIO',
        ...(isAssociation
          ? [
              'RAGIONE SOCIALE UFFICIO',
              'UFFICIO',
              'EMAIL PEC UFFICIO',
              'PERCENTUALE UFFICIO',
            ]
          : []),
          'DATA COMPENSO',
          'ESERCIZIO DI RIFERIMENTO',
        'QUOTA RESPONSABILE MATURATA',
        'INVITATO A FATTURARE',
      ],
    ];

    services.forEach(
      ({
        pod_pdr,
        admin,
        customer,
        supplier,
        adminShare,
        earnedAdminShare,
        dateFee,
      }) => {
        CSVData.push([
          pod_pdr,
          admin?.businessName,
          admin?.taxCode,
          supplier?.businessName,
          supplier?.taxCode,
          customer?.type === 'AZIENDA'
            ? customer?.businessName
            : customer?.type === 'PRIVATO'
            ? `${customer?.name} ${customer?.surname}`
            : customer?.name,
          customer?.vatCode ? customer?.vatCode : customer?.taxCode,
          customer?.studioNumber ? customer?.studioNumber : '-',
          ...(isAssociation
            ? [
                customer.office?.businessName,
                customer.office?.office,
                customer.office?.email,
                data.profile[0]?.officePerc || 0,
              ]
            : []),
            dateFee ? moment(dateFee).format('DD/MM/YYYY') : '',
            data.accountability,
          formatCurrency(+adminShare),
          formatCurrency(+earnedAdminShare),
        ]);
      }
    );
    CSVData.push([
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      ...(isAssociation ? ['-', '-', '-', '-'] : []),
      '-',
      '-',
      formatCurrency(+amount),
    ]);

    return CSVData || [];
  }

  if (iafType === IAFType.INSURANCES) {
    const CSVData = [
      [
        'RAMI',
        'CODICE POLIZZA',
        'ETICHETTA POLIZZA',
        'RESPONSABILE',
        'CODICE FISCALE RESPONSABILE',
        'CLIENTE',
        'CODICE FISCALE CLIENTE',
        'N. STUDIO',
        ...(isAssociation
          ? [
              'RAGIONE SOCIALE UFFICIO',
              'UFFICIO',
              'EMAIL PEC UFFICIO',
              'PERCENTUALE UFFICIO',
            ]
          : []),
        'TIPO AGENZIA',
        'ESERCIZIO DI RIFERIMENTO',
        'DATA INIZIO',
        'DATA FINE',
        'PREMIO LORDO',
        'PREMIO NETTO',
        'PROFITTO SUB AGENZIA',
      ],
    ];

    services.forEach(
      ({
        branch,
        policy_code,
        insurance_code,
        admin,
        start_date,
        end_date,
        gross,
        net,
        adminShare,
        agencyType,
        customer,
      }) => {
        CSVData.push([
          branch,
          policy_code,
          insurance_code,
          admin?.businessName,
          admin?.taxCode,
          customer?.name,
          customer?.vatCode ? customer?.vatCode : customer?.taxCode,
          customer?.studioNumber ? customer?.studioNumber : '-',
          ...(isAssociation
            ? [
                customer.office?.businessName,
                customer.office?.office,
                customer.office?.email,
                data.profile[0]?.officePerc || 0,
              ]
            : []),
          agencyType,
          data.accountability,
          moment(start_date).format('DD/MM/yyyy'),
          moment(end_date).format('DD/MM/yyyy'),
          formatCurrency(+gross),
          formatCurrency(+net),
          formatCurrency(+adminShare),
        ]);
      }
    );
    CSVData.push([
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      ...(isAssociation ? ['-', '-', '-', '-'] : []),
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      formatCurrency(+amount),
    ]);
    return CSVData || [];
  }

  if (iafType === IAFType.SIGNALS) {
    const signalType = services[0]?.signalTypeInfo?.code || '';

    if (signalType === 'CONDO') {
      const CSVData = [
        [
          'CODICE FISCALE BENEFICIARIO',
          'NOME BENEFICIARIO',
          'CODICE FISCALE SEGNALATORE',
          'NOME SEGNALATORE',
          'NOME CONDOMINIO',
          'P.IVA CONDOMINIO',
          'DATA RENDICONTAZIONE',
          'ESERCIZIO DI RIFERIMENTO',
          'TOTALE FATTURA',
          'INVITATO A FATTURARE',
          'NOTE',
        ],
      ];

      services.forEach(
        ({
          adminProfile,
          signalmanProfile,
          condoInfo,
          accountabilityDate,
          shareToInvoice,
          calculateAdminPerc,
          adminShare,
          notes,
        }) => {
          CSVData.push([
            adminProfile?.fiscalCode,
            adminProfile?.businessName,
            signalmanProfile?.fiscalCode || '-',
            signalmanProfile?.businessName || '-',
            condoInfo?.name || '-',
            condoInfo?.vatCode || '-',
            moment(accountabilityDate).format('DD/MM/yyyy'),
            data.accountability,
            formatCurrency(+shareToInvoice),
            formatCurrency(+adminShare),
            notes,
          ]);
        }
      );
      CSVData.push([
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        formatCurrency(+amount),
        '-',
      ]);
      return CSVData || [];
    } else if (signalType === 'RENTALS_SALES') {
      const CSVData = [
        [
          'CODICE FISCALE BENEFICIARIO',
          'NOME BENEFICIARIO',
          'CODICE FISCALE SEGNALATORE',
          'NOME SEGNALATORE',
          'DATA RENDICONTAZIONE',
          'TOTALE FATTURA',
          'INVITATO A FATTURARE',
          'NOTE',
        ],
      ];

      services.forEach(
        ({
          adminProfile,
          signalmanProfile,
          accountabilityDate,
          shareToInvoice,
          calculateAdminPerc,
          adminShare,
          notes,
        }) => {
          CSVData.push([
            adminProfile?.fiscalCode,
            adminProfile?.businessName,
            signalmanProfile?.fiscalCode || '-',
            signalmanProfile?.businessName || '-',
            moment(accountabilityDate).format('DD/MM/yyyy'),
            data.accountability,
            formatCurrency(+shareToInvoice),
            formatCurrency(+adminShare),
            notes,
          ]);
        }
      );
      CSVData.push([
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        formatCurrency(+amount),
        '-',
      ]);
      return CSVData || [];
    } else if (signalType === 'SPECIAL_JOBS') {
      const CSVData = [
        [
          'CODICE FISCALE BENEFICIARIO',
          'NOME BENEFICIARIO',
          'CODICE FISCALE SEGNALATORE',
          'NOME SEGNALATORE',
          'DATA RENDICONTAZIONE',
          'TOTALE FATTURA',
          'INVITATO A FATTURARE',
          'NOTE',
        ],
      ];

      services.forEach(
        ({
          adminProfile,
          signalmanProfile,
          accountabilityDate,
          shareToInvoice,
          calculateAdminPerc,
          adminShare,
          notes,
        }) => {
          CSVData.push([
            adminProfile?.fiscalCode,
            adminProfile?.businessName,
            signalmanProfile?.fiscalCode || '-',
            signalmanProfile?.businessName || '-',
            moment(accountabilityDate).format('DD/MM/yyyy'),
            data.accountability,
            formatCurrency(+shareToInvoice),
            formatCurrency(+adminShare),
            notes,
          ]);
        }
      );
      CSVData.push([
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        formatCurrency(+amount),
        '-',
      ]);
      return CSVData || [];
    }
    return [];
  }

  return [['NO DATA']];
};

const getCSVNetworkData = ({ data = {}, services = [] }) => {
  const { amount, iafType } = data;

  if (iafType === IAFType.INVOICES || iafType === IAFType.INVOICES_SUB_ADMINS) {
    const CSVData = [
      [
        'NUMERO FATTURA',
        'DATA',
        'RESPONSABILE',
        'CODICE FISCALE RESPONSABILE',
        'FORNITORE',
        'CODICE FISCALE FORNITORE',
        'CLIENTE',
        'CODICE FISCALE CLIENTE',
        'N. STUDIO',
        'MESE RENDICONTAZIONE',
        'IMPONIBILE',
        'QUOTA RESPONSABILE (%)',
        'QUOTA RESPONSABILE MATURATA',
        'INVITATO A FATTURARE',
      ],
    ];

    services.forEach(
      ({
        number,
        date,
        admin,
        supplier,
        customer,
        accountability,
        net,
        adminShare,
        earnedCommercialShare,
      }) => {
        CSVData.push([
          number,
          moment(date).format('DD/MM/yyyy'),
          admin?.businessName,
          admin?.taxCode,
          supplier?.businessName,
          supplier?.taxCode,
          customer?.type === 'AZIENDA'
            ? customer?.businessName
            : customer?.type === 'PRIVATO'
            ? `${customer?.name} ${customer?.surname}`
            : customer?.name,
          customer?.vatCode ? customer?.vatCode : customer?.taxCode,
          customer?.studioNumber ? customer?.studioNumber : '-',
          accountability ? moment(accountability).format('MM/yyyy') : '',
          data.accountability,
          formatCurrency(+net),
          formatCurrency(iafType === IAFType.INVOICES ? 10 : 1),
          formatCurrency(+adminShare),
          formatCurrency(+earnedCommercialShare),
        ]);
      }
    );
    CSVData.push([
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      formatCurrency(+amount),
    ]);
    return CSVData || [];
  }

  if (iafType === IAFType.ENERGY) {
    const CSVData = [
      [
        'POD_PDR',
        'RESPONSABILE',
        'CODICE FISCALE RESPONSABILE',
        'FORNITORE',
        'CODICE FISCALE FORNITORE',
        'CLIENTE',
        'CODICE FISCALE CLIENTE',
        'N. STUDIO',
        'QUOTA RESPONSABILE MATURATA',
        'INVITATO A FATTURARE',
      ],
    ];

    services.forEach(
      ({
        pod_pdr,
        admin,
        customer,
        supplier,
        adminShare,
        earnedCommercialShare,
      }) => {
        CSVData.push([
          pod_pdr,
          admin?.businessName,
          admin?.taxCode,
          supplier?.businessName,
          supplier?.taxCode,
          customer?.type === 'AZIENDA'
            ? customer?.businessName
            : customer?.type === 'PRIVATO'
            ? `${customer?.name} ${customer?.surname}`
            : customer?.name,
          customer?.vatCode ? customer?.vatCode : customer?.taxCode,
          customer?.studioNumber ? customer?.studioNumber : '-',
          formatCurrency(+adminShare),
          formatCurrency(+earnedCommercialShare),
        ]);
      }
    );
    CSVData.push([
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      formatCurrency(+amount),
    ]);
    return CSVData || [];
  }

  if (iafType === IAFType.RECURRENT_ENERGY) {
    const CSVData = [
      [
        'POD_PDR',
        'RESPONSABILE',
        'CODICE FISCALE RESPONSABILE',
        'FORNITORE',
        'CODICE FISCALE FORNITORE',
        'CLIENTE',
        'CODICE FISCALE CLIENTE',
        'N. STUDIO',
        'QUOTA RESPONSABILE MATURATA',
        'INVITATO A FATTURARE',
      ],
    ];

    services.forEach(
      ({
        pod_pdr,
        admin,
        customer,
        supplier,
        adminShare,
        earnedCommercialShare,
      }) => {
        CSVData.push([
          pod_pdr,
          admin?.businessName,
          admin?.taxCode,
          supplier?.businessName,
          supplier?.taxCode,
          customer?.type === 'AZIENDA'
            ? customer?.businessName
            : customer?.type === 'PRIVATO'
            ? `${customer?.name} ${customer?.surname}`
            : customer?.name,
          customer?.vatCode ? customer?.vatCode : customer?.taxCode,
          customer?.studioNumber ? customer?.studioNumber : '-',
          formatCurrency(+adminShare),
          formatCurrency(+earnedCommercialShare),
        ]);
      }
    );
    CSVData.push([
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      '-',
      formatCurrency(+amount),
    ]);
    return CSVData || [];
  }

  if (iafType === IAFType.INSURANCES) {
    const CSVData = [
      [
        'RAMI',
        'CODICE POLIZZA',
        'ETICHETTA POLIZZA',
        'RESPONSABILE',
        'CODICE FISCALE RESPONSABILE',
        'NOME CLIENTE',
        'CODICE FISCALE CLIENTE',
        'N. STUDIO',
        'TIPO AGENZIA',
        'ESERCIZIO DI RIFERIMENTO',
        'DATA INIZIO',
        'DATA FINE',
        'PREMIO LORDO',
        'PREMIO NETTO',
        'ROY LA PANORAMICA',
        'PROFITTO SUB AGENZIA',
        'QUOTA RESPONSABILE MATURATA',
        'INVITATO A FATTURARE',
      ],
    ];

    services.forEach(
      ({
        branch,
        policy_code,
        insurance_code,
        admin,
        start_date,
        end_date,
        gross,
        net,
        earnedCommercialShare,
        agencyType,
        customer,
      }) => {
        CSVData.push([
          branch,
          policy_code,
          insurance_code,
          admin?.businessName,
          admin?.taxCode,
          customer?.name,
          customer?.vatCode ? customer?.vatCode : customer?.taxCode,
          customer?.studioNumber ? customer?.studioNumber : '-',
          agencyType,
          data.accountability,
          moment(start_date).format('DD/MM/yyyy'),
          moment(end_date).format('DD/MM/yyyy'),
          formatCurrency(+gross),
          formatCurrency(+net),
          formatCurrency(+earnedCommercialShare),
        ]);
      }
    );
    CSVData.push([
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      formatCurrency(+amount),
    ]);
    return CSVData || [];
  }

  return [['NO DATA']];
};

export default ExportIAFCSVField;
